/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { clearAuth } from './auth';
import { RUNTIME_CONFIG } from '../config/runtimeConfig';
import { getOrCreateDeviceId } from './deviceId';

export type RequestJsonOptions = {
  skipAuth?: boolean;
  skipCsrf?: boolean;
  suppressUnauthorizedRedirect?: boolean;
};

export const API_BASE_URL = RUNTIME_CONFIG.apiBaseUrl.replace(/\/$/, '');

export class RequestError extends Error {
  status: number;
  code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'RequestError';
    this.status = status;
    this.code = code;
  }
}

const CSRF_HEADER_NAME = 'x-csrf-token';
const DEVICE_ID_HEADER_NAME = 'x-device-id';
const CSRF_ENDPOINT = `${API_BASE_URL}/auth/csrf`;
let csrfTokenCache = '';

const isUnsafeMethod = (method?: string) => {
  const normalized = String(method || 'GET').toUpperCase();
  return normalized === 'POST' || normalized === 'PUT' || normalized === 'PATCH' || normalized === 'DELETE';
};

const setCsrfTokenCache = (value: unknown) => {
  const token = String(value || '').trim();
  if (token) {
    csrfTokenCache = token;
  }
};

export const clearCsrfTokenCache = () => {
  csrfTokenCache = '';
};

export const requestJson = async <T>(
  url: string,
  init?: RequestInit,
  options?: RequestJsonOptions
): Promise<T> => {
  const headers = new Headers(init?.headers || {});
  headers.set('Accept', 'application/json');
  if (init?.body && !headers.has('Content-Type') && !(init.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }
  if (!headers.has(DEVICE_ID_HEADER_NAME)) {
    headers.set(DEVICE_ID_HEADER_NAME, getOrCreateDeviceId());
  }

  if (!options?.skipAuth && !options?.skipCsrf && isUnsafeMethod(init?.method)) {
    if (!csrfTokenCache) {
      const tokenResponse = await requestJson<{ csrfToken: string }>(
        CSRF_ENDPOINT,
        { method: 'GET' },
        { skipCsrf: true }
      );
      setCsrfTokenCache(tokenResponse?.csrfToken);
    }
    if (csrfTokenCache) {
      headers.set(CSRF_HEADER_NAME, csrfTokenCache);
    }
  }

  const res = await window.fetch(url, {
    ...init,
    headers,
    credentials: 'include',
    cache: 'no-store',
    referrerPolicy: 'no-referrer',
  });

  let body: any = {};
  const contentType = res.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    body = await res.json().catch(() => ({}));
  } else if (res.status !== 204) {
    body = await res.text().catch(() => '');
  }

  setCsrfTokenCache((body as { csrfToken?: unknown })?.csrfToken);

  if (res.status === 401 && !options?.skipAuth) {
    clearCsrfTokenCache();
    clearAuth();
    if (!options?.suppressUnauthorizedRedirect && window.location.pathname !== '/login') {
      window.location.replace('/login');
    }
  }

  if (!res.ok) {
    const message = typeof body?.message === 'string' ? body.message : `Request failed: ${res.status}`;
    const code = typeof body?.code === 'string' ? body.code : undefined;
    throw new RequestError(message, res.status, code);
  }

  return body as T;
};

export const requestBlob = async (
  url: string,
  init?: RequestInit,
  options?: RequestJsonOptions
): Promise<Blob> => {
  const headers = new Headers(init?.headers || {});
  if (!headers.has(DEVICE_ID_HEADER_NAME)) {
    headers.set(DEVICE_ID_HEADER_NAME, getOrCreateDeviceId());
  }

  const res = await window.fetch(url, {
    ...init,
    headers,
    credentials: 'include',
    cache: 'no-store',
    referrerPolicy: 'no-referrer',
  });

  if (res.status === 401 && !options?.skipAuth) {
    clearAuth();
    if (!options?.suppressUnauthorizedRedirect && window.location.pathname !== '/login') {
      window.location.replace('/login');
    }
  }

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new RequestError(text || `Request failed: ${res.status}`, res.status);
  }

  return res.blob();
};
